/**
 * RatingMoments: the store rating sheet, asked for after a look at a success.
 *
 * Headless, mounted once. Two jobs:
 *
 *   1. THE MILESTONE. Follows the same inbox query the bell polls (every
 *      minute, deduped by React Query) and, when a fresh `collection_milestone`
 *      row arrives for the signed-in account, ARMS a rating moment
 *      (lib/ratingMoments decides "fresh" and "arrived").
 *
 *   2. THE DRIVER of lib/ratingDwell. Listens for the trainer's own scrolls
 *      (touchmove, wheel), drops the moment when the app hides, and once a
 *      second asks whether the look has happened: on the results surface,
 *      scrolled then paused, or settled for half a minute. Then it asks
 *      lib/storeReview for the sheet, which still decides whether this
 *      session may ask at all. While a modal is up the moment simply waits.
 *
 * The other success, a live scan that added entries, is armed by the scan
 * sheet itself (DexLiveScanMenu) and fires here the same way.
 */
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthOptional } from "@/components/AuthProvider";
import { useSessionOptional } from "@/components/SessionProvider";
import { sessionModalOpen, useRequestRating } from "@/lib/useFirstRunPrompt";
import { pickMilestoneMoment, readMilestoneSeen, writeMilestoneSeen, type InboxRowLike } from "@/lib/ratingMoments";
import {
  RATING_TICK_MS,
  armRatingMoment,
  dropRatingMoment,
  noteRatingScroll,
  ratingMomentDue,
  takeRatingMoment,
} from "@/lib/ratingDwell";

export function RatingMoments(): null {
  const auth = useAuthOptional();
  const session = useSessionOptional();
  const accountId = auth?.account?.id ?? null;
  const requestRating = useRequestRating();
  const modalRef = useRef(false);
  modalRef.current = sessionModalOpen(session);

  const inbox = useQuery<{ notifications: InboxRowLike[] }>({
    queryKey: ["/api/notifications"],
    enabled: accountId != null,
    refetchInterval: 60_000,
  });
  const rows = inbox.data?.notifications;

  // A fresh milestone: arm the moment; the driver below fires it after the look.
  useEffect(() => {
    if (accountId == null || !rows) return;
    const seen = readMilestoneSeen();
    const { newestId, fire } = pickMilestoneMoment(rows, seen, accountId, Date.now());
    if (seen == null || seen.accountId !== accountId || seen.id !== newestId) {
      writeMilestoneSeen({ accountId, id: newestId });
    }
    if (fire) armRatingMoment("milestone", "any");
  }, [accountId, rows]);

  // The driver.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onScroll = () => noteRatingScroll(Date.now());
    const onVisibility = () => {
      if (document.visibilityState !== "visible") dropRatingMoment("hidden");
    };
    document.addEventListener("touchmove", onScroll, { capture: true, passive: true });
    document.addEventListener("wheel", onScroll, { capture: true, passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    const timer = setInterval(() => {
      if (modalRef.current) return;
      if (!ratingMomentDue(Date.now())) return;
      const pending = takeRatingMoment();
      if (pending) void requestRating(pending.moment);
    }, RATING_TICK_MS);
    return () => {
      clearInterval(timer);
      document.removeEventListener("touchmove", onScroll, { capture: true });
      document.removeEventListener("wheel", onScroll, { capture: true });
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [requestRating]);

  return null;
}
